import { useRef, useState } from 'react';

export function KrogerMembershipPage({
  onBack,
  onSubmit,
}: {
  onBack?: () => void;
  onSubmit?: (data: {
    membershipNumber: string;
    photo: File;
  }) => void | Promise<void>;
}) {
  const [membershipNumber, setMembershipNumber] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit = membershipNumber.trim().length > 0 && photo !== null;

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setPhoto(file);
    setError(null);
    setPhotoPreview((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit || !photo || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit?.({ membershipNumber: membershipNumber.trim(), photo });
    } catch {
      setError('We could not upload your image. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-[560px]">
        <div className="mb-3 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-primary">
          Step 2 of 2
        </div>

        <h1 className="font-display text-[clamp(24px,6vw,30px)] font-bold leading-[1.2] tracking-[-0.02em] text-ink mb-4">
          Verify your Kroger membership
        </h1>
        <p className="text-base leading-[1.5] text-ink-muted mb-7">
          Take a photo of your Kroger loyalty card (or the barcode in the Kroger
          app) showing your membership number.
        </p>

        <button
          type="button"
          data-testid="kroger-photo-upload-button"
          onClick={() => fileInputRef.current?.click()}
          className="mb-7 flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-[1.5px] border-dashed border-rule bg-primary-pale/40 px-6 py-10 text-center transition-colors hover:border-ink-muted"
        >
          {photoPreview ? (
            <img
              src={photoPreview}
              alt="Membership card preview"
              className="max-h-40 rounded-xl object-contain"
            />
          ) : (
            <>
              <span className="grid h-14 w-14 place-items-center rounded-xl bg-primary-pale text-primary">
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.66-.9l.82-1.2A2 2 0 0110.07 4h3.86a2 2 0 011.66.9l.82 1.2a2 2 0 001.66.9H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <circle cx="12" cy="13" r="3" />
                </svg>
              </span>
              <span className="font-display text-lg font-bold text-ink">
                Tap to take a photo
              </span>
              <span className="text-sm text-ink-muted">
                or upload from your library
              </span>
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoChange}
          className="hidden"
        />

        <label
          htmlFor="kroger-membership-number"
          className="mb-2 block text-[15px] font-bold text-ink"
        >
          Membership number <span className="text-primary">*</span>
        </label>
        <input
          id="kroger-membership-number"
          data-testid="kroger-membership-number-input"
          type="text"
          inputMode="numeric"
          value={membershipNumber}
          onChange={(event) => setMembershipNumber(event.target.value)}
          placeholder="e.g. 4 0451 12345 6"
          className="w-full rounded-xl border-[1.5px] border-rule bg-surface px-4 py-3.5 font-mono text-[15px] tracking-[0.05em] text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-primary focus:shadow-[0_0_0_3px_rgba(43,92,255,0.12)]"
        />
        <p className="mt-2.5 flex items-start gap-2 text-sm leading-[1.45] text-ink-muted">
          <svg
            className="mt-0.5 shrink-0 text-primary"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 11v5M12 8h.01"
            />
          </svg>
          Type the number exactly as it appears on your card so we can match the
          photo.
        </p>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <button
          type="button"
          data-testid="kroger-submit-button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="mt-6 w-full rounded-xl bg-primary px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-primary-deep disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Uploading…' : 'Submit & verify'}
        </button>

        <button
          type="button"
          data-testid="kroger-back-button"
          onClick={onBack}
          className="mt-4 w-full py-2 text-sm font-semibold text-ink-faint hover:text-ink-muted"
        >
          Back
        </button>
      </div>
    </div>
  );
}
